import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Tooltip } from "react-tooltip";
import { fetchCallPatternStats } from "../services/analyticsService";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig } from "./PanelHeadline";

// Calls are the one channel the chat contact-info filter cannot read, so
// this panel watches whether pairs use a free-window call as the ramp off
// the app. "Vanished" = neither member had any in-app activity in the 7
// days after their last completed call. Pairs whose last call is younger
// than 7 days are still pending and stay out of the rate.
//
// "End not recorded" (added 2026-09-21): the app writes a call's end only
// when someone presses the in-app hang-up button, so a call that dies
// with the app (killed, signal lost, phone locked) connects and then
// never closes. Those pairs are listed but stay out of the vanish rate,
// whose definition is unchanged.
const STATE_BADGE = {
    vanished: { label: "Vanished", className: "bg-danger-focus text-danger-main" },
    stayed: { label: "Stayed", className: "bg-success-focus text-success-main" },
    pending: { label: "Pending", className: "bg-neutral-200 text-neutral-600" },
    unclosed: { label: "End not recorded", className: "bg-warning-focus text-warning-600" },
};

const CallPatternStats = ({ startLoading, stopLoading }) => {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            startLoading();
            setFailed(false);
            const res = await fetchCallPatternStats();
            setData(res?.data?.data);
        } catch (err) {
            console.error("Failed to load call pattern stats", err);
            setFailed(true);
        } finally {
            stopLoading();
        }
    };

    const summary = data?.summary;
    const pairs = data?.pairs || [];
    const connected = summary?.connectedPairs ?? summary?.pairsWithCalls ?? 0;
    const unclosedCount = summary?.unclosedPairs || 0;

    const completedCallsDescription =
        `${summary?.totalAudioCalls || 0} voice, ${summary?.totalVideoCalls || 0} video. ` +
        `Ringing and rejected attempts are not counted.` +
        (summary?.totalUnrecordedCalls
            ? ` ${summary.totalUnrecordedCalls} more connected but never recorded an end; those are not in this count.`
            : "");

    const cards = [
        {
            title: "Call and vanish rate",
            value:
                summary?.callAndVanishRate === null ||
                    summary?.callAndVanishRate === undefined
                    ? "n/a"
                    : `${summary.callAndVanishRate}%`,
            description: `${summary?.vanishedPairs || 0} of ${summary?.eligiblePairs || 0
                } eligible pairs went quiet in the 7 days after their last call. Pairs whose last call is newer than 7 days are still pending and excluded.`,
            icon: "mdi:phone-remove",
            bg: "bg-danger-main",
            gradient: "bg-gradient-start-1",
        },
        {
            title: "Pairs that called",
            value: `${connected}`,
            description: `Pairs that connected at least one call. ${summary?.eligiblePairs || 0
                } are past the 7 day window and count toward the rate.`,
            icon: "mdi:account-multiple-check",
            bg: "bg-purple",
            gradient: "bg-gradient-start-2",
        },
        {
            title: "Completed calls",
            value: `${summary?.totalCompletedCalls || 0}`,
            description: completedCallsDescription,
            icon: "mdi:phone-in-talk",
            bg: "bg-success-main",
            gradient: "bg-gradient-start-4",
        },
    ];

    const unclosedNote = unclosedCount ? (
        <>
            {" "}
            <Fig>{unclosedCount}</Fig> {unclosedCount === 1 ? "pair" : "pairs"} connected a
            call that never recorded its end, usually the app closing mid-call.
        </>
    ) : null;

    return (
        <AnalyticsSection
            id="calls"
            icon="mdi:phone-outline"
            title="Call patterns"
            basis="Voice and video are the only unfiltered channel. Watch this before changing call gating."
        >

            {/* The denominator is in the sentence, not behind the dot: a bare
                "31%" is the figure most likely to be quoted without its basis. */}
            <PanelHeadline
                failed={failed}
                failedText="Could not load call data. The chat provider may be unreachable."
                empty={!summary || !connected}
                emptyText="No pairs have connected a call yet, so there is nothing to watch."
            >
                {summary?.eligiblePairs ? (
                    <>
                        <Fig>{summary?.callAndVanishRate ?? 0}%</Fig> of pairs that called went
                        quiet within a week (<Fig>{summary?.vanishedPairs ?? 0}</Fig> of{" "}
                        <Fig>{summary?.eligiblePairs ?? 0}</Fig> eligible pairs).
                        {unclosedNote}
                    </>
                ) : (
                    <>
                        <Fig>{connected}</Fig> {connected === 1 ? "pair has" : "pairs have"}{" "}
                        connected a call. None are a week past their last call yet, so the
                        vanish rate has nothing to judge.{unclosedNote}
                    </>
                )}
            </PanelHeadline>

            {failed && (
                <div className="alert alert-warning py-2 px-3 mb-3">
                    Could not load call data. The chat provider may be unreachable.
                </div>
            )}

            {summary?.cacheStale && (
                <div className="alert alert-warning py-2 px-3 mb-3">
                    The chat provider could not be reached just now, so the newest calls
                    may be missing. Everything below is from the last successful read.
                </div>
            )}

            <div className="row row-cols-lg-3 row-cols-1 gy-4 mb-4">
                {cards.map((card, idx) => (
                    <div className="col" key={idx}>
                        <div
                            className={`card shadow-none border ${card.gradient} h-100 position-relative`}
                        >
                            <div className="card-body p-20 position-relative">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <p className="fw-medium text-primary-light mb-1">
                                            {card.title}
                                        </p>
                                        <h6 className="mb-0">{card.value}</h6>
                                    </div>
                                    <div
                                        className={`w-50-px h-50-px ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                                    >
                                        <Icon
                                            icon={card.icon}
                                            className="text-white text-2xl mb-0"
                                        />
                                    </div>
                                </div>

                                <span
                                    className={`position-absolute ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                                    style={{ bottom: "8px", right: "8px", cursor: "pointer" }}
                                    data-tooltip-id={`tooltip-callpattern-${idx}`}
                                >
                                    <Icon
                                        icon="ic:baseline-info"
                                        className="text-white"
                                        style={{ fontSize: "1rem" }}
                                    />
                                </span>

                                <Tooltip
                                    id={`tooltip-callpattern-${idx}`}
                                    place="top"
                                    style={{ zIndex: "1080", maxWidth: "320px" }}
                                >
                                    {card.description}
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="table-responsive">
                <table className="table bordered-table mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Pair</th>
                            <th scope="col">Calls</th>
                            <th scope="col">Voice / video</th>
                            <th scope="col">Last call</th>
                            <th scope="col">Outcome</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pairs.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center text-secondary-light py-4">
                                    No calls yet.
                                </td>
                            </tr>
                        )}
                        {pairs.map((p, idx) => {
                            const badge = STATE_BADGE[p.state] || STATE_BADGE.pending;
                            return (
                                <tr key={idx}>
                                    <td>{(p.members || []).map((m) => m.name).join(" + ")}</td>
                                    <td>
                                        {p.completedCalls}
                                        {p.unrecordedCalls ? (
                                            <span
                                                className="text-warning-600 fw-medium"
                                                title={`plus ${p.unrecordedCalls} ${p.unrecordedType || ""} call that connected but never recorded an end`.replace("  ", " ")}
                                            >
                                                {" "}+{p.unrecordedCalls}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td>
                                        {p.audioCalls} / {p.videoCalls}
                                    </td>
                                    <td>
                                        {p.lastCallAt
                                            ? new Date(p.lastCallAt).toLocaleDateString()
                                            : "-"}
                                    </td>
                                    <td>
                                        <span
                                            className={`px-12 py-4 rounded-pill fw-medium text-sm ${badge.className}`}
                                        >
                                            {badge.label}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </AnalyticsSection>
    );
};

export default CallPatternStats;
